import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/4FaceMedia_Wortmarke_MagicWhite.svg";
import { sections as leistungenSections } from "@/data/leistungenSections";

const navLinks = [
  { label: "Home", path: "/" },
  {
    label: "Leistungen",
    path: "/leistungen",
    children: leistungenSections.map((s) => ({ label: s.shortLabel, path: `/leistungen/${s.slug}` })),
  },
  { label: "Projekte", path: "/projekte" },
  { label: "Kalender", path: "/kalender" },
  { label: "Über uns", path: "/ueber-uns" },
  { label: "Kontakt", path: "/kontakt" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 50);
      setVisible(y < 50 || y < lastY);
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled && !mobileOpen ? "glass py-3" : "bg-transparent py-6"
        } ${visible || mobileOpen ? "translate-y-0" : "-translate-y-full"}`}
      >
        <div className="container mx-auto flex items-center justify-between px-6">
          {/* Logo */}
          <Link to="/" className="relative z-[60]">
            <img src={logo} alt="4FaceMedia" className="h-7" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8 font-agency">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path || (link.children && location.pathname.startsWith(link.path + "/"));
              if (link.children) {
                return (
                  <div key={link.path} className="relative group">
                    <Link
                      to={link.path}
                      className={`text-lg font-bold italic uppercase tracking-wide transition-all duration-300 relative flex items-center gap-1 ${
                        isActive ? "text-foreground" : "text-foreground/60 hover:text-foreground"
                      }`}
                    >
                      {link.label}
                      <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                      <span
                        className={`absolute -bottom-1 left-0 h-[2px] bg-primary transition-all duration-300 ${
                          isActive ? "w-[calc(100%-1rem)]" : "w-0 group-hover:w-[calc(100%-1rem)]"
                        }`}
                      />
                    </Link>
                    <div className="absolute top-full left-0 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                      <div className="glass rounded-2xl p-3 min-w-[260px] flex flex-col gap-1">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`px-4 py-1.5 rounded-lg text-lg font-bold italic uppercase tracking-wide transition-colors ${
                              location.pathname === child.path
                                ? "text-primary bg-primary/10"
                                : "text-foreground/70 hover:text-foreground hover:bg-foreground/5"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-bold italic uppercase tracking-wide transition-all duration-300 relative group ${
                    location.pathname === link.path
                      ? "text-foreground"
                      : "text-foreground/60 hover:text-foreground"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-[2px] bg-primary transition-all duration-300 ${
                      location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-foreground p-2 relative z-[60]"
            aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
          >
            {mobileOpen ? <X size={28} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Fullscreen Overlay */}
      <div
        className={`md:hidden fixed inset-0 z-[55] flex flex-col items-center justify-start pt-28 overflow-y-auto transition-all duration-500 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{
          backgroundColor: mobileOpen ? "hsl(var(--background) / 0.92)" : "transparent",
          backdropFilter: mobileOpen ? "blur(24px)" : "none",
          WebkitBackdropFilter: mobileOpen ? "blur(24px)" : "none",
        }}
      >
        <nav className="flex flex-col items-center gap-8 pb-20">
          {navLinks.map((link, i) => (
            <div
              key={link.path}
              className="flex flex-col items-center gap-3"
              style={{
                transitionDelay: mobileOpen ? `${i * 120}ms` : "0ms",
                transform: mobileOpen ? "translateX(0) scale(1)" : "translateX(-40px) scale(0.9)",
                opacity: mobileOpen ? 1 : 0,
                filter: mobileOpen ? "blur(0px)" : "blur(6px)",
                transition: "all 700ms ease-out",
              }}
            >
              <div className="flex items-center gap-2">
                <Link
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`text-3xl font-agency font-bold italic uppercase tracking-wider ${
                    location.pathname === link.path ? "text-primary" : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {link.label}
                </Link>
                {link.children && (
                  <button
                    onClick={() => setMobileSubOpen(mobileSubOpen === link.path ? null : link.path)}
                    className="text-foreground/60 p-1"
                    aria-label="Untermenü"
                  >
                    <ChevronDown size={22} className={`transition-transform ${mobileSubOpen === link.path ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
              {link.children && mobileSubOpen === link.path && (
                <div className="flex flex-col items-center gap-1">
                  {link.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => setMobileOpen(false)}
                      className={`text-2xl font-agency font-bold italic uppercase tracking-tight ${
                        location.pathname === child.path ? "text-primary" : "text-foreground/60 hover:text-foreground"
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default Header;
